import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useHistory } from 'react-router-dom';

import Modal from '../../Components/UI/Modal/Modal';
import FirebaseImage from '../../Components/Media/FirebaseImage';
import firebase from '../../Firebase/Firebase';
import {
  acceptDemandOffer,
  answerDemandOfferClarification,
  declineDemandOffer,
} from '../../Store/Actions/actionOffer';
import { offerStatusLabel, subscribeToDemandOffers } from '../../Services/demandOffers';
import { formatDate } from '../../Utils/date';
import { useI18n } from '../../i18n/I18nProvider';

const ClientOffersModal = ({
  opened,
  demand,
  loading,
  onClose,
  acceptOffer,
  declineOffer,
  answerClarification,
}) => {
  const history = useHistory();
  const { pick, t, formatCurrency } = useI18n();
  const [offers, setOffers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState('');
  const [clarificationDrafts, setClarificationDrafts] = useState({});

  useEffect(() => {
    if (!opened || !demand?.demand_id) return undefined;
    setFetching(true);
    setError('');
    return subscribeToDemandOffers(
      firebase.firestore(),
      demand.demand_id,
      (rows) => {
        setOffers(rows);
        setFetching(false);
      },
      (subscriptionError) => {
        setError(subscriptionError?.message || pick('Impossible de charger les propositions.', 'Unable to load the proposals.'));
        setFetching(false);
      },
    );
  }, [demand?.demand_id, opened, pick]);

  const activeOffers = useMemo(
    () => offers.filter((offer) => offer.status !== 'withdrawn'),
    [offers],
  );

  const decide = async (decision, offer) => {
    if (!demand || !offer || submitting) return;
    // A request for information is not a proposal selection. The backend also
    // enforces this rule so a stale client cannot accidentally reserve a project.
    if (decision === 'accept' && offer.assessment_mode === 'more_info') return;

    const key = `${decision}:${offer.artisan_id}`;
    setSubmitting(key);
    const success = decision === 'accept'
      ? await acceptOffer(demand, offer)
      : await declineOffer(demand.demand_id, offer.artisan_id);
    setSubmitting('');
    if (success && decision === 'accept') onClose?.();
  };

  const replyToClarification = async (offer) => {
    if (!demand?.demand_id || !offer?.artisan_id || submitting) return;
    const response = String(
      clarificationDrafts[offer.artisan_id] ?? offer.client_clarification_response ?? '',
    ).trim();

    if (response.length < 2) return;

    const key = `clarification:${offer.artisan_id}`;
    setSubmitting(key);
    const success = await answerClarification(demand.demand_id, offer.artisan_id, response);
    setSubmitting('');
    if (success) {
      setClarificationDrafts((current) => ({ ...current, [offer.artisan_id]: response }));
    }
  };

  const openProfile = (offer) => {
    if (!offer?.artisan_id) return;
    onClose?.();
    history.push('/artisanboutique', { artisanId: offer.artisan_id });
  };

  return (
    <Modal opened={opened} close={() => !submitting && onClose?.()}>
      <Header>
        <div>
          <Eyebrow>{pick('Réponses des artisans', 'Artisan responses')}</Eyebrow>
          <h2>{pick('Comparez les propositions sans perdre vos options', 'Compare proposals without losing your options')}</h2>
          <p>
            {pick(
              'Répondre à une demande de précisions ne sélectionne pas l’artisan : votre projet reste ouvert et les autres propositions restent disponibles.',
              'Answering a clarification request does not select that artisan: your project stays open and all other proposals remain available.',
            )}
          </p>
        </div>
        <IconButton type="button" onClick={() => !submitting && onClose?.()} aria-label={pick('Fermer', 'Close')}><CloseOutlinedIcon /></IconButton>
      </Header>

      {fetching ? (
        <State>{pick('Chargement des propositions…', 'Loading proposals…')}</State>
      ) : error ? (
        <ErrorState>{error}</ErrorState>
      ) : activeOffers.length === 0 ? (
        <State>{pick('Aucune proposition active pour le moment.', 'No active proposal yet.')}</State>
      ) : (
        <Offers>
          {activeOffers.map((offer) => {
            const name = [offer.artisan_firstname, offer.artisan_lastname].filter(Boolean).join(' ') || pick('Artisan', 'Artisan');
            const accepted = offer.status === 'accepted';
            const pending = offer.status === 'pending';
            const moreInfo = offer.assessment_mode === 'more_info';
            const visitRequired = offer.assessment_mode === 'visit_required' || !offer.assessment_mode;
            const clarificationResponse = clarificationDrafts[offer.artisan_id]
              ?? offer.client_clarification_response
              ?? '';
            const clarificationAnswered = Boolean(String(offer.client_clarification_response || '').trim());

            return (
              <OfferCard key={offer.offer_id || offer.artisan_id} $accepted={accepted}>
                <OfferHeader>
                  <ProfileButton type="button" onClick={() => openProfile(offer)}>
                    <Avatar src={offer.artisan_profile_image} fallbackSrc="/images/user.svg" alt={`${pick('Profil de', 'Profile of')} ${name}`} />
                    <div>
                      <strong>{name}</strong>
                      <span><StorefrontOutlinedIcon /> {pick('Voir sa vitrine', 'View profile')}</span>
                    </div>
                  </ProfileButton>
                  <Status $status={offer.status}>{moreInfo && pending ? pick('Précisions demandées', 'Information requested') : offerStatusLabel(offer.status, t)}</Status>
                </OfferHeader>

                <Message>{offer.estimation_description || pick('Aucun message fourni.', 'No message provided.')}</Message>

                {moreInfo && (
                  <ClarificationBox>
                    <ClarificationHeading>
                      <strong>{pick('Précisions demandées', 'Information requested')}</strong>
                      <ClarificationState $answered={clarificationAnswered}>
                        {clarificationAnswered
                          ? pick('Réponse envoyée', 'Response sent')
                          : pick('Votre réponse est attendue', 'Waiting for your response')}
                      </ClarificationState>
                    </ClarificationHeading>
                    <Question>{offer.additional_info_request || pick('L’artisan souhaite obtenir des précisions avant de faire une proposition.', 'The artisan needs more information before making a proposal.')}</Question>

                    {pending && (
                      <ClarificationForm>
                        <label htmlFor={`clarification-${offer.artisan_id}`}>{pick('Votre réponse', 'Your response')}</label>
                        <textarea
                          id={`clarification-${offer.artisan_id}`}
                          value={clarificationResponse}
                          onChange={(event) => setClarificationDrafts((current) => ({
                            ...current,
                            [offer.artisan_id]: event.target.value,
                          }))}
                          placeholder={pick('Ex. largeur 2,40 m, hauteur 1,90 m…', 'E.g. width 2.40 m, height 1.90 m…')}
                          maxLength={1500}
                        />
                        <ClarificationFooter>
                          <small>
                            {pick(
                              'Cette réponse n’attribue pas le chantier. Vous pouvez continuer à comparer les autres artisans.',
                              'This response does not award the project. You can keep comparing other artisans.',
                            )}
                          </small>
                          <PrimaryButton
                            type="button"
                            disabled={loading || Boolean(submitting) || String(clarificationResponse).trim().length < 2}
                            onClick={() => replyToClarification(offer)}
                          >
                            {submitting === `clarification:${offer.artisan_id}`
                              ? pick('Envoi…', 'Sending…')
                              : clarificationAnswered
                                ? pick('Mettre à jour ma réponse', 'Update my response')
                                : pick('Envoyer les informations', 'Send information')}
                          </PrimaryButton>
                        </ClarificationFooter>
                      </ClarificationForm>
                    )}
                  </ClarificationBox>
                )}

                <Meta>
                  {visitRequired && offer.visit_date ? (
                    <MetaItem><CalendarTodayOutlinedIcon /><div><span>{pick('Visite proposée', 'Proposed visit')}</span><strong>{formatDate(offer.visit_date)}</strong></div></MetaItem>
                  ) : null}
                  {Number(offer.estimated_budget) > 0 ? (
                    <MetaItem><PaidOutlinedIcon /><div><span>{pick('Budget estimatif', 'Estimated budget')}</span><strong>{formatCurrency(offer.estimated_budget)}</strong><small>{pick('Indicatif, modifiable après visite', 'Indicative and adjustable after the visit')}</small></div></MetaItem>
                  ) : null}
                </Meta>

                {accepted ? (
                  <Accepted><CheckCircleOutlineOutlinedIcon /> {pick('Cette proposition a été acceptée.', 'This proposal has been accepted.')}</Accepted>
                ) : pending ? (
                  <Actions>
                    <SecondaryButton
                      type="button"
                      disabled={loading || Boolean(submitting)}
                      onClick={() => decide('decline', offer)}
                    >
                      {submitting === `decline:${offer.artisan_id}` ? pick('Refus…', 'Declining…') : pick('Ne pas retenir', 'Do not select')}
                    </SecondaryButton>
                    {!moreInfo && (
                      <PrimaryButton
                        type="button"
                        disabled={loading || Boolean(submitting)}
                        onClick={() => decide('accept', offer)}
                      >
                        {submitting === `accept:${offer.artisan_id}`
                          ? pick('Sélection…', 'Selecting…')
                          : visitRequired
                            ? pick('Retenir et organiser la visite', 'Select and arrange the visit')
                            : pick('Retenir cette proposition', 'Select this proposal')}
                      </PrimaryButton>
                    )}
                  </Actions>
                ) : null}
              </OfferCard>
            );
          })}
        </Offers>
      )}
    </Modal>
  );
};

const Header = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:1.2rem;h2{margin:.2rem 0;color:#111111;font-size:clamp(1.35rem,3vw,1.9rem);}p{max-width:44rem;margin:.45rem 0 0;color:#726b61;line-height:1.55;}`;
const Eyebrow = styled.span`color:#a88a5d;font-size:.72rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase;`;
const IconButton = styled.button`width:2.5rem;height:2.5rem;display:grid;place-items:center;flex:0 0 auto;border:0;border-radius:50%;background:#f1ece5;color:#111111;cursor:pointer;`;
const Offers = styled.div`display:grid;gap:1rem;max-height:min(68vh,48rem);overflow:auto;padding-right:.15rem;`;
const OfferCard = styled.article`display:grid;gap:.9rem;padding:1rem;border:1px solid ${({$accepted})=>$accepted?'#a9dcc1':'#e7dfd2'};border-radius:1rem;background:${({$accepted})=>$accepted?'#f3fbf6':'#fff'};box-shadow:0 .55rem 1.5rem rgba(0,0,0,.06);`;
const OfferHeader = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:.8rem;`;
const ProfileButton = styled.button`min-width:0;display:flex;align-items:center;gap:.7rem;padding:0;border:0;background:transparent;color:#111111;font:inherit;text-align:left;cursor:pointer;div{display:grid;min-width:0;}strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}span{display:flex;align-items:center;gap:.25rem;margin-top:.12rem;color:#726b61;font-size:.72rem;}svg{font-size:.9rem;}`;
const Avatar = styled(FirebaseImage)`width:3.15rem;height:3.15rem;flex:0 0 auto;border-radius:50%;object-fit:cover;background:#f1ece5;`;
const Status = styled.span`flex:0 0 auto;padding:.36rem .62rem;border-radius:999px;background:${({$status})=>$status==='accepted'?'#e5f7ed':$status==='declined'?'#f1f3f5':'#efe4cf'};color:${({$status})=>$status==='accepted'?'#1d6844':$status==='declined'?'#64737d':'#8a6f48'};font-size:.7rem;font-weight:850;`;
const Message = styled.p`margin:0;padding:.8rem;border-radius:.8rem;background:#faf8f4;color:#294155;line-height:1.6;white-space:pre-wrap;`;
const ClarificationBox = styled.section`display:grid;gap:.7rem;padding:.9rem;border:1px solid #ead29b;border-radius:.9rem;background:#fffaf0;`;
const ClarificationHeading = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.7rem;flex-wrap:wrap;color:#111;`;
const ClarificationState = styled.span`padding:.28rem .52rem;border-radius:999px;background:${({$answered})=>$answered?'#e8f7ee':'#fff0cb'};color:${({$answered})=>$answered?'#17623a':'#73510a'};font-size:.68rem;font-weight:850;`;
const Question = styled.p`margin:0;color:#5d4b2f;line-height:1.55;white-space:pre-wrap;`;
const ClarificationForm = styled.div`display:grid;gap:.45rem;label{color:#332c22;font-size:.78rem;font-weight:850;}textarea{width:100%;min-height:6.5rem;resize:vertical;padding:.8rem;border:1px solid #d8c69f;border-radius:.8rem;background:#fff;color:#111;font:inherit;line-height:1.5;}`;
const ClarificationFooter = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.8rem;small{max-width:31rem;color:#746750;line-height:1.45;}@media(max-width:34rem){align-items:stretch;flex-direction:column;}`;
const Meta = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.65rem;&:empty{display:none;}@media(max-width:34rem){grid-template-columns:1fr;}`;
const MetaItem = styled.div`display:flex;align-items:flex-start;gap:.55rem;padding:.7rem;border-radius:.75rem;background:#faf8f4;color:#111111;div{display:grid;}span{color:#74828c;font-size:.66rem;font-weight:800;text-transform:uppercase;}strong{color:#223f54;font-size:.82rem;}small{color:#74828c;font-size:.68rem;margin-top:.15rem;}svg{font-size:1rem;}`;
const Actions = styled.div`display:flex;justify-content:flex-end;gap:.65rem;@media(max-width:34rem){flex-direction:column-reverse;}`;
const BaseButton = styled.button`min-height:2.7rem;padding:.68rem .9rem;border-radius:.75rem;font:inherit;font-size:.8rem;font-weight:850;cursor:pointer;&:disabled{opacity:.55;cursor:wait;}`;
const PrimaryButton = styled(BaseButton)`border:0;background:#111111;color:#fff;`;
const SecondaryButton = styled(BaseButton)`border:1px solid #d8e0e5;background:#fff;color:#7b3832;`;
const Accepted = styled.div`display:flex;align-items:center;gap:.45rem;color:#1f6845;font-size:.82rem;font-weight:850;`;
const State = styled.div`padding:2rem;border:1px dashed #ddd3c4;border-radius:1rem;color:#726b61;text-align:center;`;
const ErrorState = styled(State)`border-color:#efc9c5;background:#fff7f6;color:#8b352f;`;

const mapStateToProps = ({ demands }) => ({ loading: Boolean(demands?.loading) });
const mapDispatchToProps = (dispatch) => ({
  acceptOffer: (demand, offer) => dispatch(acceptDemandOffer(demand, offer)),
  declineOffer: (demandId, artisanId) => dispatch(declineDemandOffer(demandId, artisanId)),
  answerClarification: (demandId, artisanId, response) => dispatch(answerDemandOfferClarification(demandId, artisanId, response)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientOffersModal);
